import { Link } from "react-router-dom";

export function AuthIcon({ name, className = "h-5 w-5" }) {
  const paths = {
    user: (
      <>
        <circle cx="12" cy="8" r="3.25" />
        <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
      </>
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="m4.5 7 7.5 5.5L19.5 7" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="3" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3m-4 4.5v2" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-5.5 9.5-5.5 9.5 5.5 9.5 5.5-3.5 5.5-9.5 5.5S2.5 12 2.5 12Z" />
        <circle cx="12" cy="12" r="2.5" />
      </>
    ),
    "eye-off": (
      <>
        <path d="m3 3 18 18M9.5 6.85A9.7 9.7 0 0 1 12 6.5c6 0 9.5 5.5 9.5 5.5a16 16 0 0 1-2.15 2.65M6.35 6.35A16.2 16.2 0 0 0 2.5 12s3.5 5.5 9.5 5.5a9.4 9.4 0 0 0 3.15-.52M10 10a2.8 2.8 0 0 0 4 4" />
      </>
    ),
    arrow: <path d="M5 12h14m-5-5 5 5-5 5" />,
    check: <path d="m5 12 4 4L19 6" />,
    spark: (
      <>
        <path d="m12 2 1.3 4.2a6.4 6.4 0 0 0 4.3 4.3L22 12l-4.4 1.5a6.4 6.4 0 0 0-4.3 4.3L12 22l-1.3-4.2a6.4 6.4 0 0 0-4.3-4.3L2 12l4.4-1.5a6.4 6.4 0 0 0 4.3-4.3L12 2Z" />
        <path d="m19 2 .4 1.2A2 2 0 0 0 20.8 4l1.2.4-1.2.4a2 2 0 0 0-1.4 1.4L19 7.5l-.4-1.3a2 2 0 0 0-1.4-1.4L16 4.4l1.2-.4a2 2 0 0 0 1.4-1.4L19 2Z" />
      </>
    ),
  };

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

const story = {
  login: {
    eyebrow: "Continue your streak",
    title: "Your next game night is one sign-in away.",
    body: "Pick up where you left off, invite the room, and turn every phone into a controller.",
    status: "Lobby ready",
  },
  register: {
    eyebrow: "Join Pocket Play",
    title: "Make the room your arcade.",
    body: "Create a player profile to collect games, host tournaments, and keep every win on the board.",
    status: "New player slot",
  },
};

const perks = ["No controller needed", "Instant multiplayer", "Play anywhere"];

export function AuthLayout({ mode, children }) {
  const copy = story[mode];
  const isLogin = mode === "login";

  return (
    <main className="relative flex flex-1 items-center overflow-hidden px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
      <div className="pointer-events-none absolute left-[8%] top-16 h-44 w-44 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-8 right-[7%] h-64 w-64 rounded-full bg-pink-500/10 blur-3xl" />

      <section className="relative mx-auto grid w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950/55 text-left shadow-[0_35px_100px_-35px_rgba(15,23,42,0.9)] ring-1 ring-white/[0.04] backdrop-blur-2xl lg:grid-cols-[0.92fr_1.08fr]">
        <div className="relative hidden min-h-[650px] overflow-hidden border-r border-white/10 bg-gradient-to-br from-indigo-500/20 via-violet-500/10 to-pink-500/15 p-10 lg:flex lg:flex-col lg:justify-between xl:p-12">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:42px_42px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />
          <div className="pointer-events-none absolute -left-20 top-1/3 h-64 w-64 rounded-full border border-cyan-300/15" />
          <div className="pointer-events-none absolute -left-10 top-[38%] h-64 w-64 rounded-full border border-pink-300/10" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.26em] text-cyan-200/80">
              <span className="grid h-8 w-8 place-items-center rounded-lg border border-cyan-300/20 bg-cyan-300/10 text-cyan-200">
                <AuthIcon name="spark" className="h-4 w-4" />
              </span>
              {copy.eyebrow}
            </div>
            <h2 className="mt-7 max-w-md text-4xl font-black leading-[1.08] tracking-[-0.03em] text-white xl:text-[2.75rem]">
              {copy.title}
            </h2>
            <p className="mt-5 max-w-md text-base leading-7 text-indigo-100/65">{copy.body}</p>
          </div>

          <div className="relative z-10 my-9 flex min-h-[215px] items-center justify-center">
            <div className="absolute h-52 w-52 rounded-full border border-cyan-300/15 bg-cyan-300/[0.03]" />
            <div className="absolute h-36 w-36 rotate-45 rounded-[2rem] border border-pink-300/15 bg-pink-400/[0.04]" />
            <div className="absolute left-0 top-5 rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 shadow-xl backdrop-blur-xl">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-200/45">Room code</p>
              <p className="mt-1 font-mono text-lg font-black tracking-[0.3em] text-cyan-200">PLAY</p>
            </div>
            <div className="absolute bottom-2 right-0 rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 shadow-xl backdrop-blur-xl">
              <p className="flex items-center gap-2 text-xs font-bold text-white">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.8)]" />
                {copy.status}
              </p>
            </div>
            <div className="relative grid h-28 w-28 place-items-center rounded-[1.75rem] border border-cyan-300/30 bg-indigo-950/80 shadow-[0_0_55px_rgba(34,211,238,0.18)] ring-1 ring-white/10">
              <img src="/logo.png" alt="" className="h-20 w-20 rounded-2xl object-cover" />
            </div>
          </div>

          <ul className="relative z-10 grid grid-cols-3 gap-3" aria-label="Pocket Play benefits">
            {perks.map((perk) => (
              <li key={perk} className="border-t border-white/10 pt-4 text-xs font-semibold leading-5 text-indigo-100/60">
                <AuthIcon name="check" className="mb-2 h-4 w-4 text-pink-300" />
                {perk}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center justify-center px-5 py-8 sm:px-10 sm:py-12 lg:px-14 xl:px-20">
          <div className="w-full max-w-lg">
            <div className="mb-9 flex items-center justify-between gap-5 border-b border-white/10 lg:mb-10">
              <Link
                to="/login"
                className={`relative flex-1 pb-4 text-center text-sm font-black transition ${isLogin ? "text-white" : "text-indigo-100/40 hover:text-indigo-100/75"}`}
                aria-current={isLogin ? "page" : undefined}
              >
                Sign in
                {isLogin && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-cyan-300 via-violet-400 to-transparent" />}
              </Link>
              <Link
                to="/register"
                className={`relative flex-1 pb-4 text-center text-sm font-black transition ${!isLogin ? "text-white" : "text-indigo-100/40 hover:text-indigo-100/75"}`}
                aria-current={!isLogin ? "page" : undefined}
              >
                Create account
                {!isLogin && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-pink-300" />}
              </Link>
            </div>

            {children}
          </div>
        </div>
      </section>
    </main>
  );
}

export function AuthField({ icon, label, hint, action, className = "", ...inputProps }) {
  return (
    <label className={`block text-left ${className}`}>
      <span className="mb-2 flex items-center justify-between gap-3 text-sm font-bold text-indigo-100/80">
        <span>{label}</span>
        {hint && <span className="text-xs font-medium text-indigo-200/35">{hint}</span>}
      </span>
      <span className="group relative block">
        <span className="pointer-events-none absolute inset-y-0 left-0 grid w-12 place-items-center text-indigo-200/35 transition group-focus-within:text-cyan-200">
          <AuthIcon name={icon} />
        </span>
        <input
          {...inputProps}
          className="h-14 w-full rounded-2xl border border-white/10 bg-white/[0.045] pl-12 pr-12 text-base font-medium text-white outline-none transition placeholder:text-indigo-100/25 hover:border-white/20 focus:border-cyan-300/60 focus:bg-cyan-300/[0.045] focus:ring-4 focus:ring-cyan-300/10"
        />
        {action}
      </span>
    </label>
  );
}

export function AuthError({ children }) {
  if (!children) return null;

  return (
    <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-300/20 bg-rose-400/10 px-4 py-3 text-sm leading-6 text-rose-100">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-300 shadow-[0_0_8px_rgba(253,164,175,0.8)]" />
      <span>{children}</span>
    </div>
  );
}

export function AuthSubmitButton({ children, disabled }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="group flex h-14 w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 px-6 text-sm font-black text-white shadow-[0_18px_40px_-18px_rgba(236,72,153,0.75)] transition duration-300 hover:-translate-y-0.5 hover:brightness-110 hover:shadow-[0_22px_50px_-18px_rgba(236,72,153,0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-55"
    >
      {children}
      {!disabled && <AuthIcon name="arrow" className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
    </button>
  );
}
